import { HotelCardLinkProperties } from "@website/accommodation-ui/hotel-card-new/hotel-card-link/hotel-card-link.types";
import {
  HotelCardPropertiesType,
  HotelInfo
} from "@website/accommodation-ui/hotel-card-new/hotel-card.types";
import { setTracking } from "@website/store/tracking";
import { addEventToDataLayer } from "@website/tracking/tracking.gtm";
import {
  DATA_LAYER_EVENTS,
  USER_TRACKING_EVENTS
} from "@website/tracking/tracking.types";
import {
  ApplicationNameType,
  addEventToTracking,
  mapProductForUserTracking
} from "@website/tracking/user";
import { useCompatRouter } from "@website/widget-v2/helpers/compat-router.hook";
import omit from "lodash-es/omit";
import toNumber from "lodash-es/toNumber";
import Link from "next/link";
import { NextRouter } from "next/router";
import { Dispatch, SetStateAction } from "react";

export const hotelCardOnClick = ({
  hotelInfo,
  index,
  router,
  setClickLoading,
  onLinkClick,
  query,
  hash,
  homePageSectionName,
  isHomePage,
  isLastMinute
}: {
  hotelInfo?: HotelInfo;
  index: HotelCardPropertiesType["index"];
  router: NextRouter | any;
  setClickLoading: Dispatch<SetStateAction<number>>;
  query?: object;
  hash?: string;
  homePageSectionName?: string;
  isLastMinute?: boolean;
  isHomePage?: boolean;
} & Pick<HotelCardLinkProperties, "onLinkClick">) => {
  if (!hotelInfo) return () => {};
  return (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    const isPricingClick = Boolean(
      (event.target as HTMLElement).closest(".hotel-card-pricing")
    );
    const isTitleClick = Boolean(
      (event.target as HTMLElement).closest(".hotel-card-title")
    );
    const isImageClick = Boolean(
      (event.target as HTMLElement).closest(".hotel-card-image")
    );

    setClickLoading(hotelInfo?.id);

    setTracking((previous) => ({
      ...previous,
      hotelPriceEngineRuleId: toNumber(hotelInfo.ruleId)
    }));

    addEventToDataLayer(DATA_LAYER_EVENTS.SELECT_ITEM, {
      items: [
        {
          item_name: hotelInfo?.hotelDetail?.hotelTitle,
          item_id: hotelInfo?.id,
          price: hotelInfo.pricing.price,
          discount: hotelInfo.pricing.price - (hotelInfo.pricing.priceOff ?? 0),
          item_brand:
            process.env.NEXT_PUBLIC_APP_NAME === "hotel-international"
              ? "IH_hotel"
              : "IRR",
          item_category: hotelInfo?.hotelDetail?.cityTitle,
          item_category2: hotelInfo?.hotelDetail?.accommodationType,
          item_category3: hotelInfo?.hotelDetail?.stars,
          item_list_name: `هتل های ${hotelInfo?.hotelDetail?.cityTitle}`,
          item_list_id: hotelInfo?.hotelDetail?.cityId,
          ranking: index
        }
      ]
    });

    if (isLastMinute) {
      addEventToTracking(USER_TRACKING_EVENTS.HOTEL_HOME_LAST_MINUTE_CLICKED, {
        product: "dom-hotel",
        hotel: hotelInfo?.hotelDetail?.hotelTitle,
        city: hotelInfo?.hotelDetail?.cityTitle,
        price: hotelInfo.pricing.price,
        position: index
      });
    }

    if ((homePageSectionName || isHomePage) && !isLastMinute) {
      const payload = {
        property_id: hotelInfo?.hotelDetail?.propertyId || 0,
        property_name: hotelInfo?.hotelDetail?.hotelTitle,
        city: hotelInfo?.hotelDetail.cityTitle,
        price: hotelInfo.pricing.price,
        position: index,
        section_name: homePageSectionName
      };
      addEventToTracking(
        USER_TRACKING_EVENTS.VILLA_HOME_PROPERTY_CARD_CLICKED,
        {
          ...payload,
          product: mapProductForUserTracking("villa")
        }
      );
    }
    if (
      ["villa", "hotel-domestic", "tour", "hotel-international"].includes(
        process.env.NEXT_PUBLIC_APP_NAME as ApplicationNameType
      )
    )
      addEventToTracking(USER_TRACKING_EVENTS.HOTEL_RESERVE_BUTTON_CLICKED, {
        ranking: index,
        date_from: String(router.query.date_from) || "",
        date_to: String(router.query.date_to) || "",
        city: hotelInfo?.hotelDetail.cityTitle,
        city_id: hotelInfo?.hotelDetail.cityId,
        hotel: hotelInfo?.hotelDetail?.hotelTitle,
        hotel_id: hotelInfo.id,
        price: hotelInfo.pricing.price,
        price_off: hotelInfo.pricing.priceOff,
        discount_percent: hotelInfo.pricing.discountPercentage,
        is_available: hotelInfo.hotelDetail.isAvailable,
        price_engine_rules: [
          {
            id: hotelInfo?.ruleId || 0,
            title: ""
          }
        ],
        product: mapProductForUserTracking(
          process.env.NEXT_PUBLIC_APP_NAME as ApplicationNameType
        )
      });
    const listing_card_click_payload = {
      accommodation_id: hotelInfo.id,
      click_area: isImageClick
        ? "image_click"
        : isTitleClick
          ? "title_click"
          : isPricingClick
            ? "price_click"
            : undefined,
      position: index
    };

    if (
      router?.pathname?.includes("map") &&
      process.env.NEXT_PUBLIC_APP_NAME === "villa"
    ) {
      addEventToTracking(USER_TRACKING_EVENTS.MAP_LISTING_CARD_CLICKED, {
        ...listing_card_click_payload,
        product: mapProductForUserTracking(
          process.env.NEXT_PUBLIC_APP_NAME as ApplicationNameType
        )
      });
    }

    if (process.env.NEXT_PUBLIC_APP_NAME === "tour") {
      onLinkClick?.();
    }
    if (
      ["website", "homepage"].includes(
        process.env.NEXT_PUBLIC_APP_NAME as string
      )
    ) {
      setClickLoading(0);
    }

    router[router.query.modal ? "replace" : "push"](
      hotelInfo?.hotelDetail?.hotelPageUrl?.href || {
        pathname: hotelInfo?.hotelDetail?.hotelPageUrl?.pathname,
        query: {
          ...omit(router.query, ["modal", "selectedHotelId"]),
          ...(hotelInfo?.hotelDetail?.hotelPageUrl?.query as object),
          ...query
        },
        hash
      }
    ).finally(() => setClickLoading(0));
  };
};

export const HotelCardLink: React.FC<HotelCardLinkProperties> = (
  properties
) => {
  const router = useCompatRouter();
  if (properties.shouldRedirectUsingJs) {
    return (
      <div
        {...omit(
          properties,
          "href",
          "prefetch",
          "replace",
          "role",
          "target",
          "title"
        )}
      >
        {properties.children}
      </div>
    );
  }

  return (
    <Link
      className={properties.className}
      href={
        properties?.href || {
          pathname: properties.pathname,
          query: properties.query
        }
      }
      prefetch
      replace={properties?.replace}
      role="link"
      target={properties?.target}
      title={properties?.title}
      itemScope
      itemType="http://schema.org/Hotel"
      onClick={hotelCardOnClick({
        query: properties?.utm,
        router,
        setClickLoading: properties?.setClickLoading,
        onLinkClick: properties?.onLinkClick,
        index: properties.index,
        hotelInfo: properties.hotelInfo,
        homePageSectionName: properties.homePageSectionName,
        isLastMinute: properties.isLastMinute
      })}
    >
      {properties.children}
    </Link>
  );
};
